import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatTimestamp } from './call-recording-player';
import { channelOf, speakerAt, toTurns } from './transcript-turns';
import type { SpeakerSpan, TurnToken, Word } from './transcript-turns';
import type { CallActivity } from './types';
import type { Id } from '@minty/shared/ids';
import { useTRPC } from '~/lib/trpc';
import { cn } from '~/lib/utils';

type Transcription = NonNullable<CallActivity['transcription']>;

/** Speaker-labelled turns, grouped from the tokens of the one open transcript —
 *  the feed carries only who was on the call. */
export function CallTranscript({
  callId,
  transcription,
  speakerTimeline,
  currentTime,
  onSeek,
}: {
  callId: Id<'calls'>;
  transcription: Transcription | null;
  speakerTimeline: Array<SpeakerSpan>;
  currentTime: number;
  onSeek: (seconds: number) => void;
}) {
  const { t } = useTranslation('tasks');
  const words = useTranscriptWords(callId);
  const speakerName = useSpeakerNames(transcription, speakerTimeline);
  /**
   * Following the playhead helps only while the reader is still watching it.
   * Scroll away to read ahead and it becomes a fight: every 250 ms the passage
   * being read is yanked back out of view. So scrolling by hand hands the
   * transcript over, and clicking a word — an explicit "I want to be here" —
   * hands it back.
   */
  const [isFollowing, setIsFollowing] = useState(true);
  const seekAndFollow = (seconds: number) => {
    setIsFollowing(true);
    onSeek(seconds);
  };

  if (!transcription || words === null) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('No transcript available')}
      </p>
    );
  }

  if (words === undefined) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('Loading transcript...')}
      </p>
    );
  }

  return (
    <TranscriptScroller onUserScroll={() => setIsFollowing(false)}>
      <WordTurns
        words={words}
        speakerName={speakerName}
        currentTime={currentTime}
        isFollowing={isFollowing}
        onSeek={seekAndFollow}
      />
    </TranscriptScroller>
  );
}

/** Only `word` tokens are seekable or highlightable. */
function WordTurns({
  words,
  speakerName,
  currentTime,
  isFollowing,
  onSeek,
}: {
  words: Array<Word>;
  speakerName: (turn: { speaker: string; startSecond: number }) => string;
  currentTime: number;
  isFollowing: boolean;
  onSeek: (seconds: number) => void;
}) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const turns = useMemo(() => toTurns(words), [words]);

  // Last word that has started — stays highlighted through trailing silence.
  const activeIndex = useMemo(() => {
    let active = -1;

    for (const [index, word] of words.entries()) {
      if (word.type !== 'word') {
        continue;
      }

      if (word.startSecond > currentTime) {
        return active;
      }

      active = index;
    }

    return active;
  }, [words, currentTime]);

  useEffect(() => {
    if (!isFollowing) {
      return;
    }

    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isFollowing]);

  return (
    <>
      {turns.map((turn, index) => (
        <TurnRow
          key={index}
          startSecond={turn.startSecond}
          speaker={speakerName(turn)}
          onSeek={onSeek}
        >
          {turn.tokens.map((token) => (
            <TranscriptToken
              key={token.index}
              token={token}
              isActive={token.index === activeIndex}
              activeRef={activeRef}
              onSeek={onSeek}
            />
          ))}
        </TurnRow>
      ))}
    </>
  );
}

function TranscriptToken({
  token,
  isActive,
  activeRef,
  onSeek,
}: {
  token: TurnToken;
  isActive: boolean;
  activeRef: React.RefObject<HTMLButtonElement | null>;
  onSeek: (seconds: number) => void;
}) {
  if (token.type !== 'word') {
    return <span className="text-muted-foreground">{token.text}</span>;
  }

  const wordRef = isActive ? activeRef : undefined;
  const wordClass = cn(
    'cursor-pointer rounded px-0.5 hover:underline',
    isActive ? 'bg-primary/15 text-foreground' : 'text-muted-foreground',
  );

  return (
    <button
      ref={wordRef}
      type="button"
      onClick={() => onSeek(token.startSecond)}
      className={wordClass}
    >
      {token.text}
    </button>
  );
}

function TranscriptScroller({
  onUserScroll,
  children,
}: {
  onUserScroll?: () => void;
  children: React.ReactNode;
}) {
  return (
    /* Wheel and touch rather than `scroll`, which cannot tell whose scroll it
       is: the auto-scroll below fires it too, so following the playhead would
       immediately switch itself off. Still absent: dragging the scrollbar, and
       scrolling by keyboard, neither of which raises these. */
    <div
      onWheel={onUserScroll}
      onTouchMove={onUserScroll}
      className="flex max-h-96 flex-col gap-4 overflow-auto pr-1"
    >
      {children}
    </div>
  );
}

function TurnRow({
  startSecond,
  speaker,
  onSeek,
  children,
}: {
  startSecond: number;
  speaker: string;
  onSeek: (seconds: number) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2 text-xs">
      <button
        type="button"
        onClick={() => onSeek(startSecond)}
        className="w-8 shrink-0 cursor-pointer text-left tabular-nums text-muted-foreground hover:underline"
      >
        {formatTimestamp(startSecond)}
      </button>
      <p className="min-w-0 flex-1">
        <span className="mr-1 font-semibold text-foreground">{speaker}:</span>
        {children}
      </p>
    </div>
  );
}

/** The timeline says who was on a channel when, so a call the assistant started
 *  and a receptionist finished names both. A turn it cannot place is numbered. */
function useSpeakerNames(
  transcription: Transcription | null,
  timeline: Array<SpeakerSpan>,
) {
  const { t } = useTranslation('tasks');
  const speakers = transcription?.speakers;

  return useMemo(() => {
    const names = { client: t('Client'), reception: t('Reception'), ai: 'AI' };
    const numbers = new Map(
      (speakers ?? []).map((speaker, index) => [speaker.id, index + 1]),
    );

    return (turn: { speaker: string; startSecond: number }) => {
      const channel = channelOf(turn.speaker);
      const span =
        channel === null
          ? null
          : speakerAt(timeline, channel, turn.startSecond);

      if (span && span.kind !== 'unknown') {
        return span.name ?? names[span.kind];
      }

      return t('Speaker {{number}}', {
        number: numbers.get(turn.speaker) ?? 1,
      });
    };
  }, [speakers, timeline, t]);
}

/** The word timings are fetched only once the transcript is opened. */
function useTranscriptWords(callId: Id<'calls'>) {
  const trpc = useTRPC();
  const { data: words } = useQuery({
    ...trpc.calls.transcription.getCallTranscriptWords.queryOptions({
      callId,
    }),
    // A finished call's transcript does not change.
    staleTime: Infinity,
  });

  return words;
}
